import React, { useEffect } from "react";

import { useMutation } from "@apollo/client";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { uniqBy } from "lodash";
import { Controller } from "react-hook-form";
import * as yup from "yup";

import Dialog, { DialogProps } from "@/components/Dialog";
import MobileInput from "@/components/MobileInput";
import { graphql } from "@/gql";
import { Gender } from "@/gql/graphql";
import useFormWithYup from "@/hooks/useFormWithYup";
import useGlobalDialog from "@/hooks/useGlobalDialog";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { formatISODate } from "@/utils/datetime";
import {
  displayError,
  getValidationErrors,
  isGraphqlError,
} from "@/utils/errors";
import { apply, maybeCall } from "@/utils/functions";
import { gtagEvent } from "@/utils/gtag";
import {
  muiCheckbox,
  muiError,
  muiField,
  muiFieldError,
} from "@/utils/hookForm";

interface Props extends DialogProps {
  afterCreate?: () => Promise<void>;
}

const validationSchema = yup
  .object({
    firstName: yup.string().label("First name").required(),
    lastName: yup.string().label("Last name").required(),
    email: yup.string().label("Email").presence().email(),
    mobileNumber: yup.string().label("Phone").presence().phone(),
    gender: yup
      .string()
      .label("Gender")
      .presence()
      .required()
      .oneOf(Object.values(Gender)),
    dateOfBirth: yup
      .date()
      .label("Date of birth")
      .typeError("${label} is an invalid date")
      .nullable()
      .min("1900")
      .max(new Date(), "${label} must not be a future date"),
    sendInvite: yup.boolean().optional(),
  })
  .defined();

type FormValues = ReturnType<typeof validationSchema.cast>;

const createClientMutation = graphql(`
  mutation CreateClient($input: CreateClientInput!) {
    createClient(input: $input) {
      client {
        id
      }
    }
  }
`);

const NewClientDialog: React.FC<Props> = ({ afterCreate, ...dialogProps }) => {
  const { alert } = useGlobalDialog();
  const snackbar = useGlobalSnackbar();
  const [createClient] = useMutation(createClientMutation);

  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    reset,
  } = useFormWithYup({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      mobileNumber: "",
      gender: "",
      dateOfBirth: null as Date | null,
      sendInvite: false,
    },
    mode: "onChange",
    validationSchema,
  });

  function onSubmit(values: FormValues) {
    return createClient({
      variables: {
        input: {
          email: values.email,
          profileAttributes: {
            firstName: values.firstName,
            lastName: values.lastName,
            gender: values.gender,
            dateOfBirth: apply(values.dateOfBirth, formatISODate),
            mobileNumber: values.mobileNumber,
          },
          shouldInvite: values.sendInvite,
        },
      },
    })
      .then(({ data }) => {
        if (data != null) {
          if (values.sendInvite) gtagEvent("invite_to_mobile_app");
          snackbar.open("Client added successfully");
          gtagEvent("add_client");
          return maybeCall(afterCreate);
        }
      })
      .catch((error) => {
        if (isGraphqlError(error) && error.extensions?.code === "INVALID") {
          const validationErrors = getValidationErrors(error);
          const baseErrors = validationErrors.filter(
            (error) => error.attribute === "base"
          );
          if (baseErrors.length !== 0) {
            alert({
              title: "Failed to add client",
              message: uniqBy(baseErrors, "message")
                .map((err) => err.message)
                .join(". "),
            });
          }
          validationErrors.forEach((error) => {
            const path = error.attribute ?? "";

            switch (error.attribute) {
              case "profile.firstName":
                setError("firstName", { message: error.fullMessage });
                break;
              case "profile.lastName":
                setError("lastName", { message: error.fullMessage });
                break;
              case "profile.gender":
                setError("gender", { message: error.fullMessage });
                break;
              case "profile.mobileNumber":
                setError("mobileNumber", { message: error.fullMessage });
                break;
              case "ghostEmail":
                setError("email", { message: error.fullMessage });
                break;
              default:
                setError(path as keyof FormValues, {
                  message: error.fullMessage,
                });
            }
          });
        } else {
          alert({
            title: "Failed to add client",
            message: displayError(error),
          });
        }
      });
  }

  useEffect(() => {
    reset();
    if (dialogProps.open) {
      gtagEvent("add_client_attempt");
    }
  }, [dialogProps.open]);

  const validEmail = watch("email").trim() !== "" && !errors.email;

  return (
    <Dialog
      {...dialogProps}
      title="Add a new client"
      titleId="new-client-dialog"
      content={
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>* Name</FormLabel>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register("firstName")}
                  {...muiFieldError(errors.firstName)}
                  placeholder="First name"
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  {...register("lastName")}
                  {...muiFieldError(errors.lastName)}
                  placeholder="Last name"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>Email</FormLabel>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register("email")}
                  {...muiFieldError(errors.email)}
                  placeholder="Enter email"
                  fullWidth
                />
              </Grid>
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>Phone</FormLabel>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="mobileNumber"
                  render={({ field, fieldState }) => (
                    <MobileInput
                      {...muiField(field)}
                      {...muiError(fieldState)}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>* Gender</FormLabel>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...register("gender")}
                  {...muiFieldError(errors.gender)}
                  defaultValue=""
                  select
                  fullWidth
                >
                  <MenuItem value={Gender.Male}>Male</MenuItem>
                  <MenuItem value={Gender.Female}>Female</MenuItem>
                </TextField>
              </Grid>
            </Grid>
            <Grid item xs={12} container spacing={2}>
              <Grid item xs={12}>
                <FormLabel>Date of birth</FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="dateOfBirth"
                  render={({
                    field: { ref, name, onChange, ...field },
                    fieldState,
                  }) => (
                    <DatePicker
                      {...field}
                      inputRef={ref}
                      disableFuture
                      onChange={(value) => onChange(value as Date)}
                      slotProps={{
                        textField: {
                          name,
                          fullWidth: true,
                          error: !!fieldState?.error,
                          helperText: fieldState?.error?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
            {validEmail && (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="sendInvite"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...muiCheckbox(field)} />}
                      label="Invite the client to use the mobile app"
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Box display="flex" marginTop={6} justifyContent="center">
                <Button type="submit" size="large" disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} /> : "Add client"}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      }
    />
  );
};

export default NewClientDialog;
